import type { SQLiteDatabase } from 'expo-sqlite';

import type { PropertyRow } from '@/db/types';
import { getActiveTasks, partitionTasks } from '@/lib/tasks';

/**
 * Renders a plain-text weekly digest of what's due, what's coming up, and
 * what got done this week. Plain text on purpose — it slots straight into
 * mailto: (no MIME hoops) and reads fine when pasted into any chat. If we
 * later add a real send pipeline (SendGrid/Resend from a future backend),
 * we can promote this same data to HTML without touching the call sites.
 */

export interface DigestData {
  subject: string;
  body: string;
}

export async function buildWeeklyDigest(
  db: SQLiteDatabase,
  property: PropertyRow
): Promise<DigestData> {
  const tasks = await getActiveTasks(db, property.id, 60);
  const partition = partitionTasks(tasks);

  const doneRow = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n
     FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ? AND t.deleted_at IS NULL AND tc.completed_at >= ?`,
    property.id,
    Date.now() - 7 * 24 * 60 * 60 * 1000
  );
  const doneCount = doneRow?.n ?? 0;

  const lines: string[] = [];
  lines.push(`Homestead — your week at ${property.name ?? property.zip ?? 'home'}`);
  lines.push('');
  lines.push(`Last 7 days: ${doneCount} task${doneCount === 1 ? '' : 's'} done.`);
  lines.push('');

  if (partition.hero) {
    lines.push('YOUR ONE THING');
    lines.push(`  • ${partition.hero.title}`);
    if (partition.hero.due_at) {
      lines.push(`    Due ${formatRelative(partition.hero.due_at)}`);
    }
    lines.push('');
  }

  if (partition.thisWeek.length > 0) {
    lines.push('THIS WEEK');
    for (const t of partition.thisWeek) {
      lines.push(`  • ${t.title}${t.due_at ? ` (${formatRelative(t.due_at)})` : ''}`);
    }
    lines.push('');
  }

  if (partition.upcoming.length > 0) {
    lines.push('COMING UP');
    for (const t of partition.upcoming.slice(0, 8)) {
      lines.push(`  • ${t.title}${t.due_at ? ` (${formatRelative(t.due_at)})` : ''}`);
    }
    lines.push('');
  }

  if (!partition.hero && partition.thisWeek.length === 0 && partition.upcoming.length === 0) {
    lines.push('Nothing scheduled in the next two months. Enjoy the breather.');
    lines.push('');
  }

  lines.push('—');
  lines.push('Sent from Homestead. Open the app to mark anything done.');

  return {
    subject: `Homestead — this week (${formatToday()})`,
    body: lines.join('\n'),
  };
}

function formatToday(): string {
  return new Date().toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatRelative(due_at: number): string {
  const ms = due_at - Date.now();
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'due today';
  if (days === 1) return 'tomorrow';
  if (days < 7) return `in ${days} days`;
  if (days < 14) return 'next week';
  const d = new Date(due_at);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
